import {
  METRIC_OPTIONS, UNION_LAYER_OPTIONS, UNIVERSE_METRIC_OPTION,
  UNION_UNIVERSE_FILTER_GROUPS, buildMetricKey,
} from '../../types/geoAnalytics'
import type { GeoMetricRow, MetricOption } from '../../types/geoAnalytics'
import type { FilterGroup, FilterGroupRequest } from '../../types/contactList'
import { uuid } from '../../lib/uuid'
import { computeDomain } from './choropleth'

export type ComparePaneSource = 'metric' | 'union' | 'universe'

export interface ComparePaneSpec {
  source: ComparePaneSource
  metricOptionKey: string | null   // 'metric' + 'union' modes
  year: number | null              // 'metric' mode, when the option requiresYear
  seedListId: string | null        // universe: quick-pick a list …
  seedSavedSearchId: string | null // … or a saved search
  filterGroups: FilterGroup[]      // universe: seeded from the pick, then editable
}

export const DEFAULT_PANE_SPEC: ComparePaneSpec = {
  source: 'metric',
  metricOptionKey: null,
  year: null,
  seedListId: null,
  seedSavedSearchId: null,
  filterGroups: [],
}

/** The MetricOption a pane renders with (drives formatting + legend). */
export function comparePaneOption(spec: ComparePaneSpec): MetricOption | null {
  if (spec.source === 'universe') return UNIVERSE_METRIC_OPTION
  if (!spec.metricOptionKey) return null
  return (
    METRIC_OPTIONS.find(o => o.key === spec.metricOptionKey)
    ?? UNION_LAYER_OPTIONS.find(o => o.key === spec.metricOptionKey)
    ?? null
  )
}

export type ComparePaneRequest =
  | { kind: 'metric'; metricKeys: string[] }
  | { kind: 'live'; filterGroups: FilterGroupRequest[]; ratioDenominatorMetricKey?: string }
  | null

/** Map a spec to a query request (scope-free — the page adds unitType/state/vintage). */
export function comparePaneRequest(spec: ComparePaneSpec): ComparePaneRequest {
  if (spec.source === 'metric') {
    const opt = METRIC_OPTIONS.find(o => o.key === spec.metricOptionKey)
    if (!opt) return null
    return { kind: 'metric', metricKeys: [buildMetricKey(opt, spec.year)] }
  }
  if (spec.source === 'union') {
    if (spec.metricOptionKey === 'union.leverage')
      return { kind: 'live', filterGroups: UNION_UNIVERSE_FILTER_GROUPS, ratioDenominatorMetricKey: 'electorate.registered' }
    if (spec.metricOptionKey === 'union.footprint')
      return { kind: 'live', filterGroups: UNION_UNIVERSE_FILTER_GROUPS }
    return null
  }
  // universe
  if (spec.filterGroups.length === 0) return null
  return { kind: 'live', filterGroups: filterGroupsToRequests(spec.filterGroups) }
}

/** Two panes share a color scale only when equal shades carry equal meaning. */
export function autoComparable(a: MetricOption | null, b: MetricOption | null): boolean {
  if (!a || !b) return false
  return a.format === b.format && a.kind === b.kind
}

/**
 * Resolve whether the two panes render on one locked scale.
 * A shared scale is only ever honest when the panes are comparable, so
 * `autoComparable` is an absolute gate: an incomparable pair is never locked,
 * even if a stale manual lock is still set. When comparable, the default is
 * locked and the user may manually unlock (`override === false`).
 */
export function resolveScalesLocked(override: boolean | null, autoComparableScales: boolean): boolean {
  return autoComparableScales && (override ?? true)
}

export function sharedDomain(
  a: MetricOption, domainA: [number, number],
  b: MetricOption, domainB: [number, number],
): [number, number] {
  if (a.format === 'count' && b.format === 'count')
    return [0, Math.max(domainA[1], domainB[1])]
  if (a.kind === 'diverging') {
    const m = Math.max(Math.abs(domainA[0]), Math.abs(domainA[1]), Math.abs(domainB[0]), Math.abs(domainB[1]))
    return [-m, m]
  }
  return [Math.min(domainA[0], domainB[0]), Math.max(domainA[1], domainB[1])]
}

/** Linear-interpolated quantile over an ascending-sorted array. */
function quantile(sorted: number[], q: number): number {
  const pos = (sorted.length - 1) * q
  const base = Math.floor(pos)
  const rest = pos - base
  return base + 1 < sorted.length ? sorted[base] + rest * (sorted[base + 1] - sorted[base]) : sorted[base]
}

/** Rounding step for legend-friendly domain ends, scaled to the span. */
function niceStep(span: number): number {
  if (span >= 5) return 1
  if (span >= 0.5) return 0.1
  return 0.01
}

function snap(x: number, step: number, dir: 'floor' | 'ceil'): number {
  const v = (dir === 'floor' ? Math.floor(x / step + 1e-9) : Math.ceil(x / step - 1e-9)) * step
  return Number(v.toFixed(step >= 1 ? 0 : step >= 0.1 ? 1 : 2))
}

/** Minimum units before trimming tails with percentiles; below this, plain min–max. */
const QUANTILE_MIN_UNITS = 20

/**
 * Fit a pane's colour domain to the data it is actually showing (compare mode, scales
 * UNLOCKED). Fixed catalogue domains like turnout's [0,100] render a state whose values
 * sit in a narrow band as one flat colour. Rules:
 *  - only visible, unsuppressed, non-null values count;
 *  - ≥ QUANTILE_MIN_UNITS values → 5th–95th percentile (outliers clamp to the ramp ends),
 *    otherwise min–max; ends snapped outward to a step scaled to the span;
 *  - diverging (lean) stays symmetric around 0 so the midpoint is still EVEN;
 *  - live counts (universe / union footprint) keep their zero-anchored computeDomain;
 *  - no data → the option's configured domain (or the count floor) via computeDomain.
 * The locked/shared-scale path does NOT use this — it keeps the configured domains.
 */
export function fitDomain(option: MetricOption, rows: GeoMetricRow[]): [number, number] {
  const isLiveCount = option.group === 'union' || option.key === UNIVERSE_METRIC_OPTION.key
  if (isLiveCount) return computeDomain(option, rows)

  const values = rows
    .filter(r => !r.suppressed && r.value != null && Number.isFinite(r.value))
    .map(r => r.value as number)
    .sort((x, y) => x - y)
  if (values.length === 0) return computeDomain(option, rows)

  const trim = values.length >= QUANTILE_MIN_UNITS
  let lo = trim ? quantile(values, 0.05) : values[0]
  let hi = trim ? quantile(values, 0.95) : values[values.length - 1]

  if (option.kind === 'diverging') {
    const m = Math.max(Math.abs(lo), Math.abs(hi))
    const step = niceStep(2 * m)
    const mm = Math.max(snap(m, step, 'ceil'), step)
    return [-mm, mm]
  }

  if (hi - lo <= 0) {
    // Flat range: widen around the single value so the ramp isn't degenerate.
    const pad = Math.max(Math.abs(lo) * 0.01, 1)
    lo -= pad
    hi += pad
  }
  const step = niceStep(hi - lo)
  return [snap(lo, step, 'floor'), snap(hi, step, 'ceil')]
}

export function filterGroupsToRequests(groups: FilterGroup[]): FilterGroupRequest[] {
  return groups.map(g => ({
    groupIndex: g.groupIndex,
    filters: g.filters.map(f => ({
      fieldName: f.fieldName,
      operator: f.operator,
      value: f.value,
      displayOrder: f.displayOrder,
    })),
  }))
}

/** Add client-side UUIDs so seeded rows are editable in FilterGroupCard. */
export function seedFilterGroups(reqs: FilterGroupRequest[]): FilterGroup[] {
  return reqs.map(g => ({
    groupIndex: g.groupIndex,
    filters: g.filters.map(f => ({
      id: uuid(),
      fieldName: f.fieldName,
      operator: f.operator,
      value: f.value,
      displayOrder: f.displayOrder,
    })),
  }))
}
